import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/firestore';
import {AngularFireAuth} from '@angular/fire/auth';
import {forkJoin, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AngularFireStorage} from "@angular/fire/storage";

interface RevenueCatListResponse<T> {
  object: 'list';
  items: T[];
  next_page: string | null;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {

  constructor(
    public afs: AngularFirestore,
    private firebaseAuth: AngularFireAuth,
    private afStorage: AngularFireStorage
  ) { }

  getUidByUserId(userId) {
    return this.afs.collection('users', ref => ref.where('id', '==', `${userId}`)).valueChanges();
  }
  getPublicGroups() {
    return this.afs.collection(`groups`).valueChanges();
  }
  deleteGroup(id) {
    return this.afs.collection(`groups`).doc(`${id}`).delete();
  }
  getUserById(id) {
    return this.afs.collection('users', (ref) => ref.where('id', '==', `${id}`)).valueChanges()
  }
  getUserByEmail(email) {
    return this.afs.collection('users', (ref) => ref.where('email', '==', `${email}`)).valueChanges()
  }
  addUserToGroup(group) {
    return this.afs.collection(`groups`).doc(`${group.id}`).update(group);
  }
  updateGroup(id, group) {
    return this.afs.collection(`groups`).doc(`${group.id}`).update(group);
  }
  createGroup(name, user) {
    const collection = this.afs.collection('groups').doc(name);
    return collection.set({
      name,
      id: name,
      users: [user.id],
      procedures: [],
      owner: { uid: user.uid, id: user.id },
    });
  }
  updateUser(userUid, credentials) {
    return this.afs.doc(`users/${userUid}/`).update(credentials);
  }
  handlePremiumRole(userUid, credentials) {
    return this.afs.doc(`users/${userUid}/`).set(credentials);
  }
  getPremiumUsers() {
    return this.afs.collection('users', (ref) => ref.where('role', '==', 'premium')).valueChanges();
  }
  getSupervisors() {
    return this.afs.collection('users', (ref) => ref.where('role', '==', 'supervisor')).valueChanges();
  }
  getCurrentUser() {
    return new Observable((observer) => {
      this.firebaseAuth.authState.subscribe( authState => {
        if (authState) {
          this.getUserByEmail(authState.email).subscribe((user:any) => {
            observer.next(user[0]);
          });
        }
      });
    })
  }
  getDeviceByName(deviceName): Observable<any> {
    return this.afs.doc(`devicesList/${deviceName}`).valueChanges();
  }
  removeDeviceFromList(deviceName) {
    return this.afs.doc(`devicesList/${deviceName}`).delete();
  }
  updateDeviceUsers(deviceName, updatedUsers) {
    return this.afs.collection('devicesList').doc(deviceName).update({users: updatedUsers});
  }
  sendEmail(uid: string, email: string, html: string | number, subject: string): Promise<void> {
    return this.afs.collection('mail').doc(uid + Date.now()).set({
      to: email,
      message: {
        html,
        subject
      }
    });
  }
  setDeveloperToList(user, usersList) {
    const data = {
      developerId: user.id ? user.id : user.developerId,
      developerEmail: user.email? user.email : user.developerEmail,
      allowedUsers: usersList,
      photoURL: user.photoURL
    }
    return this.afs.doc(`developersList/${user.id ? user.id : user.developerId}`).set(data);
  }

  getDevelopersList() {
    return this.afs.collection('developersList').valueChanges();
  }
  deleteDeveloperFromList(id) {
    return this.afs.doc(`developersList/${id}`).delete();
  }
  getRecordings(userId, sensorType) {
    return this.afs.collection(`records/${userId}/${sensorType}/`, ref => ref.orderBy('dateCreated').limitToLast(20)).valueChanges();
  }
  getRecordingsLoadMore(userId, sensorType, start) {
    return this.afs.collection(`records/${userId}/${sensorType}/`, ref => ref.orderBy('dateCreated').endBefore(start).limitToLast(20)).valueChanges();
  }
  getUsersCount() {
    // All users
    const allUsers$ = this.afs.collection('users').get().pipe(map(snap => snap.size));

    // Users with role 'supervisor'
    const supervisors$ = this.afs
      .collection('users', ref => ref.where('role', '==', 'supervisor'))
      .get()
      .pipe(map(snap => snap.size));

    // Users with role 'premium'
    const premium$ = this.afs
      .collection('users', ref => ref.where('role', '==', 'premium'))
      .get()
      .pipe(map(snap => snap.size));
    const developers$ = this.afs
      .collection('developersList')
      .get()
      .pipe(map(snap => snap.size));
    // Combine all counts in one Observable
    return forkJoin([allUsers$, supervisors$, premium$, developers$]).pipe(
      map(([total, supervisors, premium, developers]) => ({ total, supervisors, premium, developers }))
    );
  }
  setPromoCode(code) {
    const id = code.createdAt;
    return this.afs.doc(`promoCodes/id${id}`).set(code);
  }
  getPromoCodes() {
    return this.afs.collection('promoCodes').valueChanges();
  }
  setPromoCodeUser(code, email) {
    const codeId = code.createdAt;
    return this.afs.doc(`promoCodes/id${codeId}`).update({sentToEmail: email});
  }
  deletePromoCode(code) {
    const codeId = code.createdAt;
    return this.afs.doc(`promoCodes/id${codeId}`).delete();
  }
  getPromoCodeEmailEN() {
    return this.afs.doc('emailTemplates/activationCode').valueChanges();
  }
  getPromoCodeEmailDE() {
    return this.afs.doc('emailTemplates/activationCodeDE').valueChanges();
  }
  getRevenueEvents() {
    return this.afs.collection('revenueCatEvents').valueChanges();
  }
  async listRevenueCatFiles() {
    try {
      const result = await this.afStorage.storage.refFromURL('gs://revenuecat-exports-prod/revenuecat').listAll();
      return await Promise.all(
        result.prefixes.map(async folderRef => {
          const folderContent = await folderRef.listAll();
          return {
            folder: folderRef.name,
            file: folderContent.items[0]
          };
        })
      );

    } catch (error) {
      console.error('Error fetching RevenueCat files:', error);
      return [];
    }
  }
}
